import { firestore } from "../firebase.config";
import { AccountType } from "./enums";

// Where people start once they are signed in: clients on their order list,
// the office and translators in the portal (the office routes exist only for
// these roles, see App.js).
export const homePathFor = (user) => {
  const role = user && user.role;
  if (role === AccountType.OWNER || role === AccountType.SUPPORT || role === AccountType.TRANSLATER) {
    return "/office/translations";
  }
  return "/account/orders";
};

// The page a signed-out visitor was sent home from: PrivateRoute redirects to
// "/" and keeps it as the location's state.from. They go back to it after
// signing in. Only the account and office pages, never an outside address.
export const returnPathFrom = (location) => {
  const from = location && location.state && location.state.from;
  if (!from || typeof from.pathname !== "string" || !/^\/(account|office)\//.test(from.pathname)) {
    return null;
  }
  return from.pathname + (from.search || "") + (from.hash || "");
};

// Right after signing in the profile isn't loaded yet: it is read here for
// the role.
export const landingAfterSignIn = async (uid) => {
  try {
    const snap = await firestore.collection("users").doc(uid).get();
    return homePathFor(snap.exists ? snap.data() : null);
  } catch (e) {
    return "/account/orders";
  }
};

// The address shown now, as a path ("/account/orders?x=1#y").
export const currentPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

// Sends the person to their landing page once. Right after signing in, the
// sign-in form and the page behind it (Home) can both decide where to go; the
// second call to the same place is dropped instead of adding it to the
// history again.
//
// Only a call right after the first one is a duplicate (the two decide at the
// same moment). A later one is not: right after signing in the page can
// bounce back to Home (the private route sees "not signed in" for a moment and
// sends the person to "/"), and Home's call must then be allowed through, or
// the person stays on the home page while the address says /account/orders
// (2026-10-10; it was dropped for 3 s).
const DUPLICATE_MS = 400;
let lastLanding = { path: null, at: 0 };
export const goToLanding = (transitionTo, path) => {
  if (signingOut) {
    return;
  }
  const now = Date.now();
  if (currentPath() === path || (lastLanding.path === path && now - lastLanding.at < DUPLICATE_MS)) {
    return;
  }
  lastLanding = { path, at: now };
  transitionTo(path);
};

// Signing out sends the person to "/" first and then closes the session, which
// takes a moment. Home still saw them signed in and sent them on to their
// landing page (/office/translations, /account/orders), and once the session
// closed that page no longer existed for them: a 404 (2026-10-12). While a
// sign-out is under way there is no landing page to go to.
let signingOut = false;
export const beginSignOut = () => {
  signingOut = true;
};
// Called once the session is closed and the profile cleared.
export const endSignOut = () => {
  signingOut = false;
};
